/**
 * TodoBits — the row-level pieces every to-dos lens shares (the to-dos
 * upgrade, Claude Design handoff 2026-10-04).
 *
 *   TodoCheck     the 18px check: the stroke draws in over 120ms. On email /
 *                 text / call rows it is a SKIP control instead (those complete
 *                 when the message goes out — Michael: "replaced with a skip
 *                 icon and a warning before approved skipping").
 *   TodoChip      the SOURCE-row chip grammar: hairline, radius 4, optional
 *                 uppercase qualifier, dashed = unset or only suggested.
 *   TodoDueChip   the due chip, coloured by bucket (late = off-track, today and
 *                 "Needs a date" = paprika, suggested = dashed); opens the
 *                 two-rail HubDatePop.
 *   TodoFromChip  where it came from (Deal · Oz Realty, EOS · Ops L10, …).
 *   TodoAvatar    initials on the person's colour.
 *   TodoVerb      the action's verb button; disabled WITH its reason, never
 *                 hidden.
 *   TodoPickerChip  a chip that opens the canonical TodoMenu.
 */
import React, { useRef, useState } from 'react';
import s from './todosHub.module.css';
import TodoTypeIcon, { todoTypeTileClass } from '../TodoTypeIcon';
import tc from '../todoContainer.module.css';
import TodoMenu, { type TodoMenuOption } from '../TodoMenu';
import { HubDatePop } from '../../hub/HubDatePop';
import { todoDueBucket, todoDueLabel } from '../todoDue';
import { stringToColor } from '../../shared/DataTable/tableTheme';
import type { TodoItem } from './todoModel';
import { completesOnSend } from './todoModel';
import type { TodoType } from '../../shared/todoTypes';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

// ── Check / Skip ─────────────────────────────────────────────────────────

export function TodoCheck({ type, checked, onComplete, onSkip, small, label }: {
  type: TodoType;
  checked?: boolean;
  onComplete: () => void;
  onSkip: () => void;
  small?: boolean;
  label: string;
}): React.ReactElement {
  if (completesOnSend(type)) {
    const what = type === 'email' ? 'email' : type === 'text' ? 'text' : 'call';
    return (
      <button
        type="button"
        className={cx(s.check, small && s.checkSmall)}
        aria-label={`Skip ${label}`}
        title={`This ${what} to-do completes when it is sent — skip it instead`}
        onClick={(e) => { e.stopPropagation(); onSkip(); }}
      >
        <span className={s.skipBox}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 5l10 7-10 7z" /><path d="M19 5v14" />
          </svg>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      aria-label={`Complete ${label}`}
      className={cx(s.check, small && s.checkSmall, checked && s.checkOn)}
      onClick={(e) => { e.stopPropagation(); onComplete(); }}
    >
      <span className={s.checkBox}>
        <svg width={small ? 10 : 12} height={small ? 10 : 12} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path className={s.checkMark} d="M5 12.5l4.2 4.2L19 7" stroke="var(--on-brand)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>
  );
}

export function DoneTile(): React.ReactElement {
  return (
    <span className={s.doneTile} aria-hidden>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.2 4.2L19 7" stroke="var(--on-brand)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </span>
  );
}

// ── Type tile ────────────────────────────────────────────────────────────

export function TypeTile({ type, size = 24 }: { type: string; size?: number }): React.ReactElement {
  return (
    <span
      className={cx(tc.tile, todoTypeTileClass(type))}
      style={{ width: size, height: size, borderRadius: size > 28 ? 6 : 4 }}
      aria-hidden
    >
      <TodoTypeIcon type={type} />
    </span>
  );
}

// ── Chips ────────────────────────────────────────────────────────────────

export interface TodoChipProps {
  children: React.ReactNode;
  qualifier?: string | null;
  dashed?: boolean;
  tone?: 'off' | 'paprika' | null;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  caret?: boolean;
  className?: string;
  style?: React.CSSProperties;
  chipRef?: React.Ref<HTMLButtonElement>;
}

export function TodoChip({ children, qualifier, dashed, tone, onClick, title, caret, className, style, chipRef }: TodoChipProps): React.ReactElement {
  const cls = cx(s.chip, dashed && s.chipDashed, tone === 'off' && s.chipOff, tone === 'paprika' && s.chipPaprika, className);
  const body = (
    <>
      {qualifier && <span className={s.chipQualifier}>{qualifier}</span>}
      <span className={s.chipText}>{children}</span>
      {caret && <span className={s.chipCaret} aria-hidden>▾</span>}
    </>
  );
  if (!onClick) return <span className={cls} style={style} title={title}>{body}</span>;
  return (
    <button ref={chipRef} type="button" className={cls} style={style} title={title} onClick={(e) => { e.stopPropagation(); onClick(e); }}>
      {body}
    </button>
  );
}

/** The due chip's tone + dashing for a to-do. */
export function dueTone(t: Pick<TodoItem, 'due' | 'suggested'>): { tone: 'off' | 'paprika' | null; dashed: boolean } {
  const b = todoDueBucket(t.due);
  return {
    tone: b === 'overdue' ? 'off' : (b === 'today' || b === 'none') ? 'paprika' : null,
    dashed: t.suggested || b === 'none',
  };
}

export function TodoDueChip({ todo, onPick, onClear, readOnly }: {
  todo: Pick<TodoItem, 'due' | 'suggested' | 'title'>;
  onPick?: (isoDay: string) => void;
  onClear?: () => void;
  readOnly?: boolean;
}): React.ReactElement {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { tone, dashed } = dueTone(todo);
  const label = todoDueLabel(todo.due, todo.suggested);
  if (readOnly || !onPick) {
    return <TodoChip tone={tone} dashed={dashed} className={s.tabular}>{label}</TodoChip>;
  }
  return (
    <>
      <TodoChip
        chipRef={ref}
        tone={tone}
        dashed={dashed}
        className={s.tabular}
        title={todo.suggested ? 'Suggested — click to confirm or change' : todo.due ? 'Change the due date' : 'Give it a date'}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </TodoChip>
      <HubDatePop
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        value={todo.due}
        suggested={todo.suggested}
        header={todo.suggested ? 'Suggested' : todo.due ? 'Due' : 'Set a date'}
        onPick={onPick}
        onClear={onClear}
      />
    </>
  );
}

export function TodoFromChip({ todo, meId }: { todo: Pick<TodoItem, 'from' | 'assignedById' | 'assigneeId'>; meId: number | null }): React.ReactElement | null {
  const f = todo.from;
  if (f.kind === 'Typed in') {
    // Typed in by the person it belongs to: say so. Typed in by someone else:
    // the "from <name>" beside it already says it, so no chip.
    const byOwner = todo.assignedById == null || todo.assignedById === todo.assigneeId;
    return byOwner ? <TodoChip qualifier={todo.assigneeId === meId ? 'Me' : null}>Typed in</TodoChip> : null;
  }
  if (f.kind === 'Repeat') {
    return (
      <TodoChip title="Repeats">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <RepeatGlyph size={11} />{f.name || 'Repeats'}
        </span>
      </TodoChip>
    );
  }
  return <TodoChip qualifier={f.kind === 'Other' ? null : f.kind}>{f.name || f.kind}</TodoChip>;
}

export function RepeatGlyph({ size = 13 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: 'none' }}>
      <path d="M17 2l4 4-4 4" /><path d="M3 11V9a4 4 0 014-4h14" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a4 4 0 01-4 4H3" />
    </svg>
  );
}

// ── Avatar ───────────────────────────────────────────────────────────────

export function TodoAvatar({ name, size = 22 }: { name: string | null | undefined; size?: number }): React.ReactElement {
  const full = (name || '?').trim() || '?';
  const ini = full.split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
  return (
    <span
      className={s.avatar}
      title={full}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: full === '?' ? undefined : stringToColor(full) }}
    >
      {ini}
    </span>
  );
}

// ── Verb ─────────────────────────────────────────────────────────────────

export function TodoVerb({ verb, reason, onRun, title }: { verb: string; reason: string | null; onRun: () => void; title?: string }): React.ReactElement {
  return (
    <span className={s.verbCell}>
      <button
        type="button"
        className={cx(s.btn, s.btnSmall, s.btnVerb)}
        disabled={!!reason}
        title={reason || title || verb}
        onClick={(e) => { e.stopPropagation(); onRun(); }}
      >
        {verb}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></svg>
      </button>
      {reason && <span className={s.verbReason} title={reason}>{reason}</span>}
    </span>
  );
}

// ── Picker chip (chip + the canonical TodoMenu) ─────────────────────────

export function TodoPickerChip({ children, header, options, value, onPick, dashed, qualifier, width = 'default', filterPlaceholder, title }: {
  children: React.ReactNode;
  header: string;
  options: TodoMenuOption[];
  value: string | number | null;
  onPick: (value: string | number | null) => void;
  dashed?: boolean;
  qualifier?: string | null;
  width?: 'default' | 'wide';
  filterPlaceholder?: string;
  title?: string;
}): React.ReactElement {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <TodoChip chipRef={ref} dashed={dashed} qualifier={qualifier} caret title={title} onClick={() => setOpen((v) => !v)}>
        {children}
      </TodoChip>
      <TodoMenu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={ref as React.RefObject<HTMLElement>}
        header={header}
        options={options}
        value={value}
        onPick={(v) => { setOpen(false); onPick(v); }}
        width={width}
        filterPlaceholder={filterPlaceholder}
      />
    </>
  );
}
